import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { readStoredUser } from "../utils/sessionUser";
import { getOnboardingStepDefinitions } from "./onboardingI18n";

const OnboardingFlowContext = createContext(null);
const ONBOARDING_PROGRESS_STORAGE_KEY = "palantir_onboarding_progress";

function getScopeKey(user) {
  const userId = String(user?.id || "anonymous");
  const businessUnitId = String(user?.active_business_unit_id || "0");
  return `${userId}:${businessUnitId}`;
}

function readStoredProgress(scopeKey, stepDefinitions) {
  if (typeof window === "undefined" || !scopeKey) {
    return [];
  }

  try {
    const rawValue = localStorage.getItem(ONBOARDING_PROGRESS_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : {};
    const scopedProgress = parsed?.[scopeKey];

    if (!Array.isArray(scopedProgress?.completedSteps)) {
      return [];
    }

    const normalizedSteps = scopedProgress.completedSteps.flatMap((stepId) => {
      if (stepId === "rooms") {
        return ["rooms-counts", "rooms-grid"];
      }

      return [stepId];
    });

    return normalizedSteps.filter((stepId, index, array) => {
      if (array.indexOf(stepId) !== index) {
        return false;
      }

      return stepDefinitions.some((step) => step.id === stepId);
    });
  } catch (_error) {
    return [];
  }
}

function writeStoredProgress(scopeKey, completedSteps) {
  if (typeof window === "undefined" || !scopeKey) {
    return;
  }

  try {
    const rawValue = localStorage.getItem(ONBOARDING_PROGRESS_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : {};
    parsed[scopeKey] = {
      completedSteps,
    };
    localStorage.setItem(ONBOARDING_PROGRESS_STORAGE_KEY, JSON.stringify(parsed));
  } catch (_error) {
    // Ignore local storage issues and keep the flow in memory.
  }
}

function getCurrentStepIdFromPath(pathname, stepDefinitions) {
  const matchingStep = stepDefinitions.find(
    (step) => pathname === `/onboarding/${step.path}` || pathname.startsWith(`/onboarding/${step.path}/`)
  );

  return matchingStep?.id || stepDefinitions[0]?.id || "language";
}

export function OnboardingFlowProvider({ children, steps: incomingSteps }) {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionUser = readStoredUser();
  const scopeKey = getScopeKey(sessionUser);
  const stepDefinitions = useMemo(
    () =>
      Array.isArray(incomingSteps) && incomingSteps.length
        ? incomingSteps
        : getOnboardingStepDefinitions(),
    [incomingSteps]
  );
  const [completedSteps, setCompletedSteps] = useState(() => readStoredProgress(scopeKey, stepDefinitions));
  const [stepCommandStates, setStepCommandStates] = useState({});
  const currentStepId = getCurrentStepIdFromPath(location.pathname, stepDefinitions);

  useEffect(() => {
    setCompletedSteps(readStoredProgress(scopeKey, stepDefinitions));
  }, [scopeKey, stepDefinitions]);

  useEffect(() => {
    writeStoredProgress(scopeKey, completedSteps);
  }, [completedSteps, scopeKey]);

  const isStepComplete = useCallback(
    (stepId) => completedSteps.includes(stepId),
    [completedSteps]
  );

  const isStepAccessible = useCallback(
    (stepId) => {
      const stepIndex = stepDefinitions.findIndex((step) => step.id === stepId);

      if (stepIndex <= 0) {
        return true;
      }

      const previousSteps = stepDefinitions.slice(0, stepIndex);
      return previousSteps.every(
        (step) => completedSteps.includes(step.id) && !stepCommandStates[step.id]?.isDirty
      );
    },
    [completedSteps, stepCommandStates, stepDefinitions]
  );

  useEffect(() => {
    if (!isStepAccessible(currentStepId)) {
      navigate(`/onboarding/${stepDefinitions[0]?.path || "language"}`, { replace: true });
    }
  }, [currentStepId, isStepAccessible, navigate, stepDefinitions]);

  const markStepComplete = useCallback((stepId) => {
    setCompletedSteps((currentSteps) =>
      currentSteps.includes(stepId) ? currentSteps : [...currentSteps, stepId]
    );
  }, []);

  const markStepIncomplete = useCallback((stepId) => {
    setCompletedSteps((currentSteps) => {
      if (!currentSteps.includes(stepId)) {
        return currentSteps;
      }

      return currentSteps.filter((id) => id !== stepId);
    });
  }, []);

  const setStepCommandState = useCallback((stepId, nextState) => {
    if (!stepId) {
      return;
    }

    setStepCommandStates((currentStates) => {
      const currentStepState = currentStates[stepId] || {};
      const resolvedState =
        typeof nextState === "function" ? nextState(currentStepState) : nextState;

      return {
        ...currentStates,
        [stepId]: {
          ...currentStepState,
          ...resolvedState,
        },
      };
    });
  }, []);

  const clearStepCommandState = useCallback((stepId) => {
    if (!stepId) {
      return;
    }

    setStepCommandStates((currentStates) => {
      if (!(stepId in currentStates)) {
        return currentStates;
      }

      const nextStates = { ...currentStates };
      delete nextStates[stepId];
      return nextStates;
    });
  }, []);

  const goToStep = useCallback(
    (stepId) => {
      const targetStep = stepDefinitions.find((step) => step.id === stepId);

      if (!targetStep || !isStepAccessible(stepId)) {
        return;
      }

      navigate(`/onboarding/${targetStep.path}`);
    },
    [isStepAccessible, navigate, stepDefinitions]
  );

  const value = useMemo(
    () => ({
      steps: stepDefinitions,
      currentStepId,
      completedSteps,
      isStepComplete,
      isStepAccessible,
      currentStepCommandState: stepCommandStates[currentStepId] || null,
      markStepComplete,
      markStepIncomplete,
      setStepCommandState,
      clearStepCommandState,
      goToStep,
    }),
    [
      clearStepCommandState,
      completedSteps,
      currentStepId,
      goToStep,
      isStepAccessible,
      isStepComplete,
      markStepComplete,
      markStepIncomplete,
      setStepCommandState,
      stepDefinitions,
      stepCommandStates,
    ]
  );

  return <OnboardingFlowContext.Provider value={value}>{children}</OnboardingFlowContext.Provider>;
}

export function useOnboardingFlow() {
  const context = useContext(OnboardingFlowContext);

  if (!context) {
    throw new Error("useOnboardingFlow must be used within an OnboardingFlowProvider");
  }

  return context;
}
